import { Component, Input, OnInit } from '@angular/core';
import System from 'framework/System';
import { CustomerAccountGroupComponent } from '../../../shared/components/customer-account-group/customer-account-group.component';
import CustomerAccountTypes = Boo.Objects.Enums.CustomerAccountTypes;
import interfaces = app.managecustomer.components.interfaces;
import { SaveTypes } from 'app/models/enums/SaveTypes';
import { CustomerAccountRouteFactory } from 'app/customeraccount/CustomerAccountRouteFactory';
import { CustomerAccountService } from '../../../services/customer-account.service';
import { WebsiteService } from '../../../services/website.service';
import { PreAuditDetailService } from '../../../services/pre-audit-detail.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { GoogleIntegrationService } from '../../../services/google-integration.service';
import Utils from '../../../shared/utils';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import ImplementationOwners = Boo.Objects.Enums.ImplementationOwners;
import { ErrorMessages } from 'app/models/ErrorMessages';

export interface WebsiteAccessOptions extends app.managecustomer.components.interfaces.CustomerAccountGroupOptions {
    customer: CustomerObservable;
    showContentManagementSystem: boolean;
}

/*
 * Adds external access note to the save and validation for the customer account group.
 */
@Component({
    selector: 'app-components-managecustomer-websiteaccess',
    templateUrl: './website-access.component.html'
})
export class WebsiteAccessComponent extends CustomerAccountGroupComponent implements OnInit {
    @Input() options: WebsiteAccessOptions;
    @Input() currentImplementationOwner = ImplementationOwners.NotSelected;
    customer: CustomerObservable;
    validation: KnockoutObservable<any> = ko.observable(false);
    groupValidation: KnockoutObservable<any>;
    contentManagementSystem: Boo.Objects.ContentManagementSystem;
    showContentManagementSystem: boolean;
    customerHasWebsite = false;
    currentUser: Boo.Objects.User;
    showAccessInvitePending = false;

    constructor(
        customerAccountRouteFactory: CustomerAccountRouteFactory,
        customerAccountService: CustomerAccountService,
        websiteService: WebsiteService,
        private preAuditDetailService: PreAuditDetailService,
        sessionStorageService: SessionStorageService,
        private googleIntegrationService: GoogleIntegrationService
    ) { super(customerAccountRouteFactory, customerAccountService, websiteService, sessionStorageService); }

    ngOnInit() {
        this.customer = this.options.customer;
        this.showSaveButton = this.options.showSaveButton;
        this.showContentManagementSystem = this.options.showContentManagementSystem;

        if (this.options.publicApiCallback) {
            this.options.publicApiCallback(this.getPublicApi());
        }

        this.isLoading = true;
        forkJoin([
            this.sessionStorageService.getUser(),
            this.websiteService.customerHasWebsite(this.customer.CustomerId()),
            this.googleIntegrationService.getIntegrationSummary(this.customer.CustomerId())
                .pipe(catchError(() => of(null)))
        ])
        .pipe(finalize(() => this.isLoading = false))
        .subscribe(([currentUser, customerHasWebsite, integrationSummary]) => {
            this.customerHasWebsite = customerHasWebsite;
            this.showAccessInvitePending = !!integrationSummary && integrationSummary.IsAccessInvitePending;
            this.validation.extend({
                validation: {
                    validator: (val: number): boolean => {
                        let isValid: boolean = val >= 0;
                        if (this.currentImplementationOwner === ImplementationOwners.Boostability && !this.hasAccountAndSiteId) {
                            isValid = false;
                            const filteredComponents: interfaces.CustomerAccountViewModel[] = _.filter(this.components, (component: interfaces.CustomerAccountViewModel) => {
                                return component.customerAccount.CustomerAccountTypeId() === CustomerAccountTypes.CMSAdminAccess ||
                                    component.customerAccount.CustomerAccountTypeId() === CustomerAccountTypes.FTP;
                            });
                            _.each(filteredComponents, (component: interfaces.CustomerAccountViewModel) => {
                                const account: Boo.Objects.CustomerAccount = ko.mapping.toJS(component.customerAccount);
                                const passwordIsSet: boolean = !!account.Password || !!account.SetPassword;
                                if (!!account.Username && !!account.Url && passwordIsSet) {
                                    isValid = true;
                                }
                            });
                        }
                        return isValid;
                    },
                    message: 'Login credentials for CMS or FTP are required if Boostability is making onsite edits for the customer.'
                }
            });
            this.groupValidation = ko.validatedObservable([this.validation]);
            ko.validation.registerExtenders();
            this.currentUser = currentUser;

            return super.ngOnInit();
        });
    }

    getPublicApi(): app.interfaces.ISave {
        return {
            save: (saveType: SaveTypes): JQueryPromise<void> => { return this.save(saveType); },
            confirmSave: (saveType: SaveTypes): JQueryPromise<boolean> => { return this.confirmSave(saveType); }
        };
    }

    contentManagementSystemChanged(cms: Boo.Objects.ContentManagementSystem): void {
        this.contentManagementSystem = cms;
    }

    confirmSave(saveType: SaveTypes): JQueryPromise<boolean> {
        return System.resolvedPromise(true);
    }

    save(saveType?: SaveTypes): JQueryPromise<void> {
        // this has to be here for the validation to update in the UI
        this.validation.notifySubscribers();

        const promises: JQueryPromise<void>[] = [];
        promises.push(this.saveLogins(saveType));
        promises.push(this.saveCustomerCMS());

        // Saving CMS Admin credentials advances the Google Integration workflow out of NeedsCredentials,
        // which clears the access-invite-pending state — refresh the banner so it dismisses without a reload.
        return $.when<any>(...promises).done(() => {
            if (this.showAccessInvitePending) {
                this.refreshAccessInvitePending();
            }
        });
    }

    saveLogins(saveType?: SaveTypes): JQueryPromise<void> {
        if (!this.groupValidation.isValid()) {
            this.groupValidation.errors.showAllMessages();
            return System.autoRejectedPromise(ErrorMessages.ValidationFailed);
        } else {
            return super.save(saveType);
        }
    }

    saveCustomerCMS(): JQueryPromise<any> {
        // Only try saving the CMS if it's set in the UI and can be edited
        if (this.contentManagementSystem) {
            return Utils.wrapDfd(this.preAuditDetailService.updateContentManagementSystem(
                this.customer.CustomerId(),
                this.contentManagementSystem.ContentManagementSystemId));
        }
        return System.emptyPromise();
    }

    private refreshAccessInvitePending(): void {
        this.googleIntegrationService.getIntegrationSummary(this.customer.CustomerId())
            .pipe(catchError(() => of(null)))
            .subscribe(summary => this.showAccessInvitePending = !!summary && summary.IsAccessInvitePending);
    }
}
